import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLES = ["student", "faculty", "department_head", "hospital_admin"];
const STATUSES = ["active", "inactive", "suspended", "pending_verification"];

const EMPTY = {
  first_name: "", last_name: "", email: "", mobile: "",
  employee_id: "", role_code: "student",
  department_id: "", designation: "", reporting_manager_id: "",
  joining_date: "", account_status: "active",
  create_login: true, auto_generate_password: true, password: "",
};

export default function StaffFormPage({ mode = "create" }) {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [generatedPw, setGeneratedPw] = useState(null);

  const loadLookups = useCallback(async () => {
    const [d, dg, m] = await Promise.all([
      api.get("/org/departments"),
      api.get("/org/designations").catch(() => ({ data: { data: { designations: [] } } })),
      api.get("/org/staff", { params: { page: 1, page_size: 200 } }),
    ]);
    setDepartments(d.data.data.departments);
    setDesignations(dg.data.data.designations || []);
    setManagers(m.data.data.users.filter((u) => ["hospital_admin", "faculty", "department_head"].includes(u.role_code)));
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  useEffect(() => {
    if (mode !== "edit" || !staffId) return;
    setLoading(true);
    api.get(`/org/staff/${staffId}`).then(({ data }) => {
      const u = data.data.user;
      setForm({
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        email: u.email || "",
        mobile: u.mobile || "",
        employee_id: u.employee_id || "",
        role_code: u.role_code || "student",
        department_id: u.department_id || "",
        designation: u.designation || "",
        reporting_manager_id: u.reporting_manager_id || "",
        joining_date: u.joining_date || "",
        account_status: u.account_status || "active",
        create_login: !!u.login_created,
        auto_generate_password: false,
        password: "",
      });
    }).catch((err) => toast.error(formatApiError(err))).finally(() => setLoading(false));
  }, [staffId, mode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setGeneratedPw(null);
    try {
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        employee_id: form.employee_id.trim() || null,
        role_code: form.role_code,
        department_id: form.department_id || null,
        designation: form.designation || null,
        reporting_manager_id: form.reporting_manager_id || null,
        joining_date: form.joining_date || null,
        account_status: form.account_status,
      };
      if (mode === "create") {
        body.create_login = form.create_login;
        body.auto_generate_password = form.create_login && form.auto_generate_password;
        if (form.create_login && !form.auto_generate_password) body.password = form.password;
        const { data } = await api.post("/org/staff", body);
        if (data.data.generated_login_id) {
          // Student auto-provision: show BOTH the friendly login ID
          // (usually the first name) AND the matching password so the
          // institute admin can hand them straight to the learner.
          setGeneratedPw(`${data.data.generated_login_id} / ${data.data.generated_password}`);
          toast.success(`Staff created. Login ID: ${data.data.generated_login_id} · Password: ${data.data.generated_password}`);
        } else if (data.data.generated_password) {
          setGeneratedPw(data.data.generated_password);
          toast.success("Staff created. Copy the generated password before you leave.");
        } else {
          toast.success("Staff created successfully.");
          navigate("/app/hospital/staff");
        }
      } else {
        await api.put(`/org/staff/${staffId}`, body);
        toast.success("Staff updated.");
        navigate(`/app/hospital/staff/${staffId}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isEdit = mode === "edit";

  return (
    <DashboardLayout title={isEdit ? "Edit staff" : "Add staff"} breadcrumbs={["My Institute", "Staff", isEdit ? "Edit" : "Add"]}>
      <div className="mx-auto max-w-4xl space-y-6" data-testid={ADMIN.staffFormRoot}>
        <Link to="/app/hospital/staff" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Staff
        </Link>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6">
            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Basic details</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1"><Label>First name *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid={ADMIN.staffFormFirst} /></div>
                <div className="space-y-1"><Label>Last name <span className="text-[10px] font-normal text-slate-400">(optional)</span></Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid={ADMIN.staffFormLast} placeholder="Leave blank if single-name" /></div>
                <div className="space-y-1"><Label>Email *</Label><Input type="email" value={form.email} disabled={isEdit} onChange={(e) => set("email", e.target.value)} data-testid={ADMIN.staffFormEmail} /></div>
                <div className="space-y-1"><Label>Mobile *</Label><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid={ADMIN.staffFormMobile} /></div>
                <div className="space-y-1"><Label>Employee ID</Label><Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} data-testid={ADMIN.staffFormEmpId} placeholder="e.g. EMP1001" /></div>
                <div className="space-y-1"><Label>Joining date</Label><Input type="date" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} data-testid={ADMIN.staffFormJoining} /></div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Role & assignment</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Role *</Label>
                  <Select value={form.role_code} onValueChange={(v) => set("role_code", v)}>
                    <SelectTrigger data-testid={ADMIN.staffFormRole}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Department</Label>
                  <Select value={form.department_id || "none"} onValueChange={(v) => set("department_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={ADMIN.staffFormDept}><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Designation</Label>
                  <Select value={form.designation || "none"} onValueChange={(v) => set("designation", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={ADMIN.staffFormDesignation}><SelectValue placeholder="Select designation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {designations.map((d) => <SelectItem key={d.id} value={d.designation_name}>{d.designation_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Reporting manager</Label>
                  <Select value={form.reporting_manager_id || "none"} onValueChange={(v) => set("reporting_manager_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={ADMIN.staffFormManager}><SelectValue placeholder="Select manager" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {managers.filter((m) => m.id !== staffId).map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.full_name} ({ROLE_LABELS[m.role_code]})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Account status</Label>
                  <Select value={form.account_status} onValueChange={(v) => set("account_status", v)}>
                    <SelectTrigger data-testid={ADMIN.staffFormStatus}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {!isEdit && (
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Login</h3>
                <div className="mt-3 space-y-3">
                  <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Create login for this staff</div>
                      <div className="text-xs text-slate-500">They can sign in with the credentials you set below.</div>
                    </div>
                    <Switch checked={form.create_login} onCheckedChange={(v) => set("create_login", v)} data-testid={ADMIN.staffFormCreateLogin} />
                  </div>
                  {form.create_login && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Auto-generate password</div>
                          <div className="text-xs text-slate-500">A strong password will be created and shown once.</div>
                        </div>
                        <Switch checked={form.auto_generate_password} onCheckedChange={(v) => set("auto_generate_password", v)} data-testid={ADMIN.staffFormAutoPw} />
                      </div>
                      {!form.auto_generate_password && (
                        <div className="space-y-1">
                          <Label>Password *</Label>
                          <Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid={ADMIN.staffFormPassword} placeholder="Min 8 chars, mixed case + number + symbol" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}

            {generatedPw && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="flex items-center gap-2 font-bold text-amber-800"><KeyRound className="h-4 w-4" /> Generated password</div>
                <div className="mt-1 font-mono text-base text-amber-900">{generatedPw}</div>
                <div className="mt-1 text-xs text-amber-700">This will not be shown again. Share it securely with the user.</div>
                <button className="mt-2 rounded bg-amber-700 px-3 py-1 text-xs font-semibold text-white" onClick={() => navigate("/app/hospital/staff")}>Continue</button>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <button onClick={() => navigate("/app/hospital/staff")} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
                data-testid={ADMIN.staffFormSubmit}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit ? "Save changes" : "Create staff"}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
